import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Grid, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, Select, MenuItem, Chip,
  TextField, IconButton, Tooltip, Button, Drawer, Link
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// UK VAT — ACCRUAL basis. A faithful replica of the cash-basis VATReturnReport, hitting the
// /api/accounting/vat-accrual/* endpoints. The ONLY thing the basis changes is the input side:
// input VAT is recognised at INVOICE / INCURRED date (SCM supplier_invoices — editable — plus
// ads at incurred), not at bank-payment date. Output is identical to the cash screen.
const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
const BASE = `${API_URL}/api/accounting/vat-accrual`;

function fmt(n) {
  if (n === 0 || n === null || n === undefined) return '£0.00';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n));
}

function quarterRange(yearQ) {
  const [y, q] = yearQ.split('-Q').map(Number);
  const startMonth = (q - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  const start = `${y}-${String(startMonth).padStart(2, '0')}-01`;
  const endDate = new Date(y, endMonth, 0).getDate();
  const end = `${y}-${String(endMonth).padStart(2, '0')}-${endDate}`;
  return { from: start, to: end };
}

const CURRENT_Q = (() => {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
})();

// Drill-down configuration — same shape as the cash screen. Output configs are identical;
// input configs source from supplier_invoices (SCM) + ads at incurred.
const DRILLDOWN_CONFIGS = {
  'shopify-sales': {
    title: 'Shopify UK sales — per order',
    endpoint: '/details/shopify-sales',
    note: 'One row per UK Shopify order with VAT > 0. Recognised at the order (tax-point) date — identical on both bases.',
    columns: [
      { key: 'event_date', label: 'Date' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'order_name', label: 'Order' },
      { key: 'currency', label: 'Cur' },
      { key: 'gross', label: 'Gross', align: 'right', money: true },
      { key: 'net', label: 'Net', align: 'right', money: true },
      { key: 'vat', label: 'VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'vat',
  },
  'shopify-refunds': {
    title: 'Shopify UK refunds — per refund',
    endpoint: '/details/shopify-refunds',
    note: 'Refund VAT computed from the parent order rate.',
    columns: [
      { key: 'refund_date', label: 'Refund Date' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'order_id', label: 'Order ID' },
      { key: 'refund_amt', label: 'Refund Amt', align: 'right', money: true },
      { key: 'computed_refund_vat', label: 'Computed VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'computed_refund_vat',
  },
  'amazon-uk': {
    title: 'Amazon.co.uk — per order/event',
    endpoint: '/details/amazon-uk',
    note: 'One row per Amazon order + event. Tax + ShippingTax + GiftWrapTax make up the VAT total; facilitated orders are excluded.',
    columns: [
      { key: 'posted_date', label: 'Posted' },
      { key: 'amazon_order_id', label: 'Order ID' },
      { key: 'event_name', label: 'Event' },
      { key: 'currency', label: 'Cur' },
      { key: 'net_revenue', label: 'Net', align: 'right', money: true },
      { key: 'vat_total', label: 'VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'vat_total',
  },
  'wholesale-uk': {
    title: 'Wholesale UK sales — per order',
    endpoint: '/details/wholesale-uk',
    note: 'One row per recognized GB wholesale order (retail_orders — native + imported invoice rows), stored VAT at the tax point.',
    columns: [
      { key: 'tax_point_date', label: 'Tax Point' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'source', label: 'Source' },
      { key: 'order_name', label: 'Order' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'currency', label: 'Cur' },
      { key: 'net', label: 'Net', align: 'right', money: true },
      { key: 'tax', label: 'VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'tax',
  },
  'output-all': {
    title: 'Box 1 / Box 6 — all output transactions',
    endpoint: '/details/output-all',
    note: 'Every output-side transaction across Shopify UK sales, refunds, Amazon.co.uk and wholesale (most recent 2000). Output is tax-point recognised, so identical to the cash screen.',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'component', label: 'Component' },
      { key: 'reference', label: 'Order / Ref' },
      { key: 'detail', label: 'Detail' },
      { key: 'currency', label: 'Cur' },
      { key: 'net', label: 'Net', align: 'right', money: true },
      { key: 'vat', label: 'VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'vat',
  },
  'input-all': {
    title: 'Box 4 / Box 7 — all input rows (accrual)',
    endpoint: '/details/input-all',
    note: 'Every input-side row on the ACCRUAL basis: supplier invoices from SCM (per bill, at invoice date) plus Meta/TikTok/Amazon ads and Amazon fees at the month incurred. Sum of Input VAT ≈ Box 4 (within rounding pennies — per-row vs per-month rounding).',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'component', label: 'Component' },
      { key: 'reference', label: 'Vendor / Type' },
      { key: 'detail', label: 'Treatment / Detail' },
      { key: 'currency', label: 'Cur' },
      { key: 'gross', label: 'Gross', align: 'right', money: true },
      { key: 'vat', label: 'Input VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'vat',
  },
  'net-all': {
    title: 'Box 5 — all transactions, signed (accrual)',
    endpoint: '/details/net-all',
    note: 'Output VAT positive, input VAT negative — the sum is Box 5 (net payable). Reverse-charge legs net to zero.',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'component', label: 'Component' },
      { key: 'reference', label: 'Reference' },
      { key: 'detail', label: 'Detail' },
      { key: 'currency', label: 'Cur' },
      { key: 'vat', label: 'VAT (signed)', align: 'right', money: true, bold: true },
    ],
    sumKey: 'vat',
  },
  'supplier-invoices': {
    title: 'Supplier invoices (SCM) — per bill, at invoice date',
    endpoint: '/details/supplier-invoices',
    note: 'UK supplier invoices from the SCM-owned supplier_invoices table (imported from QuickBooks, editable in SCM) — NOT read from quickbooks.* directly. Vendor-rule VAT treatment applied. Sum of Input VAT feeds Box 4.',
    columns: [
      { key: 'txn_date', label: 'Date' },
      { key: 'vendor', label: 'Vendor' },
      { key: 'doc_number', label: 'Doc' },
      { key: 'currency', label: 'Cur' },
      { key: 'total_amount', label: 'Total', align: 'right', money: true },
      { key: 'recorded_tax', label: 'Recorded Tax', align: 'right', money: true },
      { key: 'vat_treatment', label: 'Treatment' },
      { key: 'computed_input_vat', label: 'Input VAT', align: 'right', money: true, bold: true },
      { key: 'reverse_charge_vat', label: 'RC VAT', align: 'right', money: true },
    ],
    sumKey: 'computed_input_vat',
  },
  'ads-input': {
    title: 'Ad spend input VAT — by month + channel (at incurred)',
    endpoint: '/details/ads-input',
    note: 'Meta / TikTok / Amazon ad input VAT recognised at the month the spend was incurred (from ads_table). Google is captured via its supplier invoice, so it is not here. Month-grain is the finest reliable ad granularity.',
    columns: [
      { key: 'period_month', label: 'Month' },
      { key: 'channel', label: 'Channel' },
      { key: 'treatment', label: 'Treatment' },
      { key: 'net_spend_gbp', label: 'Net Spend', align: 'right', money: true },
      { key: 'input_vat', label: 'Input VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'input_vat',
  },
  'amazon-fees': {
    title: 'Amazon seller fees — by month + type (estimate)',
    endpoint: '/details/amazon-fees',
    note: 'Amazon seller fees at incurred; fee amounts are VAT-inclusive gross so estimated VAT = gross ÷ 6. Invoices live in Seller Central’s Tax Document Library.',
    columns: [
      { key: 'period_month', label: 'Month' },
      { key: 'fee_type', label: 'Fee Type' },
      { key: 'gross_fees_gbp', label: 'Gross Fees', align: 'right', money: true },
      { key: 'computed_est_vat', label: 'Est. VAT', align: 'right', money: true, bold: true },
    ],
    sumKey: 'computed_est_vat',
  },
};

function fmtCell(v, money) {
  if (v === null || v === undefined || v === '') return '';
  if (money) return fmt(v);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}

export default function VATAccrualReport() {
  const [period, setPeriod] = useState(CURRENT_Q);
  const _initRange = quarterRange(CURRENT_Q);
  const [customFrom, setCustomFrom] = useState(_initRange.from);
  const [customTo, setCustomTo] = useState(_initRange.to);
  const [summary, setSummary] = useState(null);
  const [vsCash, setVsCash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [drill, setDrill] = useState(null);

  const token = () => localStorage.getItem('token');
  const getRange = () => (period === 'custom' ? { from: customFrom, to: customTo } : quarterRange(period));
  const periodLabel = period === 'custom' ? `${customFrom} → ${customTo}` : period;

  const openDrill = async (configKey) => {
    const cfg = DRILLDOWN_CONFIGS[configKey];
    if (!cfg) return;
    const { from, to } = getRange();
    setDrill({ configKey, rows: null, loading: true, error: null });
    try {
      const res = await axios.get(`${BASE}${cfg.endpoint}?from=${from}&to=${to}&limit=2000`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.data.success) setDrill({ configKey, rows: res.data.data || [], loading: false, error: null });
      else setDrill({ configKey, rows: [], loading: false, error: res.data.error || 'Failed to load' });
    } catch (e) {
      setDrill({ configKey, rows: [], loading: false, error: e.response?.data?.error || e.message });
    }
  };
  const closeDrill = () => setDrill(null);

  const downloadExcel = async () => {
    const { from, to } = getRange();
    setDownloading(true);
    try {
      const res = await axios.get(`${BASE}/export?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token()}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vat-accrual-${period === 'custom' ? `${customFrom}_to_${customTo}` : period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      alert(`Excel export failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    const { from, to } = getRange();
    try {
      const [sumRes, vcRes] = await Promise.all([
        axios.get(`${BASE}/summary?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token()}` } }),
        axios.get(`${BASE}/vs-cash?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => ({ data: { success: false } })),
      ]);
      if (sumRes.data.success) setSummary(sumRes.data.data);
      else setError(sumRes.data.error);
      if (vcRes.data.success) setVsCash(vcRes.data.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (period === 'custom' && (!customFrom || !customTo)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const agg = (summary || []).reduce((a, m) => ({
    box1: (a.box1 || 0) + Number(m.box1_output_vat),
    box4: (a.box4 || 0) + Number(m.box4_input_vat),
    box6: (a.box6 || 0) + Number(m.box6_net_sales),
    box7: (a.box7 || 0) + Number(m.box7_net_purchases),
    shopify_output: (a.shopify_output || 0) + Number(m.shopify_output_vat),
    shopify_refunds: (a.shopify_refunds || 0) + Number(m.shopify_refund_vat),
    amazon_output: (a.amazon_output || 0) + Number(m.amazon_uk_shipment_vat) + Number(m.amazon_uk_refund_vat),
    wholesale_output: (a.wholesale_output || 0) + Number(m.wholesale_output_vat || 0),
    wholesale_zero_net: (a.wholesale_zero_net || 0) + Number(m.wholesale_zero_vat_net || 0),
    supplier_input: (a.supplier_input || 0) + Number(m.supplier_input_vat || 0),
    meta_ads: (a.meta_ads || 0) + Number(m.meta_ads_vat || 0),
    tiktok_ads: (a.tiktok_ads || 0) + Number(m.tiktok_ads_vat || 0),
    amazon_ads: (a.amazon_ads || 0) + Number(m.amazon_ads_vat || 0),
    amazon_fees: (a.amazon_fees || 0) + Number(m.amazon_fees_vat || 0),
    reverse_charge: (a.reverse_charge || 0) + Number(m.reverse_charge_vat || 0),
  }), {});

  const box3 = (agg.box1 || 0);
  const box5 = (agg.box1 || 0) - (agg.box4 || 0);

  const vcAgg = vsCash ? vsCash.reduce((a, m) => ({
    accrual_box1: (a.accrual_box1 || 0) + Number(m.accrual_box1),
    cash_box1: (a.cash_box1 || 0) + Number(m.cash_box1),
    accrual_box4: (a.accrual_box4 || 0) + Number(m.accrual_box4),
    cash_box4: (a.cash_box4 || 0) + Number(m.cash_box4),
  }), {}) : null;

  const years = [2024, 2025, 2026].flatMap(y => [1, 2, 3, 4].map(q => `${y}-Q${q}`)).reverse();

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error) return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ mr: 1 }}>UK VAT Return — Accrual (Preview)</Typography>
        <Chip size="small" color="primary" variant="outlined" label="Invoice / incurred basis" />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined" size="small"
          startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={downloadExcel} disabled={downloading}
        >
          {downloading ? 'Building…' : 'Download Excel'}
        </Button>
        <Select value={period} onChange={(e) => setPeriod(e.target.value)} size="small" sx={{ minWidth: 150 }}>
          {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          <MenuItem value="custom">Custom range…</MenuItem>
        </Select>
        {period === 'custom' && (
          <>
            <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
              value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} sx={{ width: 160 }} />
            <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
              value={customTo} onChange={(e) => setCustomTo(e.target.value)} sx={{ width: 160 }} />
            <Button variant="contained" size="small" onClick={load} disabled={!customFrom || !customTo}>Apply</Button>
          </>
        )}
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Accrual basis.</strong> VAT is recognised when a cost or sale is <strong>incurred</strong>, not when it is paid.
        Input VAT comes from the <strong>SCM-owned supplier invoices</strong> (imported from QuickBooks, editable in SCM)
        at invoice date, plus ad spend at the month incurred — never from bank payments. Output (Shopify / Amazon / wholesale)
        is tax-point recognised and identical to the cash screen. <strong>Preview only</strong> — not filed with HMRC.
      </Alert>

      {/* VAT100 BOXES */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>VAT100 Boxes for {periodLabel}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click a box row to see all its underlying transactions.</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>Box</TableCell><TableCell>Label</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover onClick={() => openDrill('output-all')} sx={{ cursor: 'pointer' }}><TableCell>1</TableCell><TableCell>VAT due on sales and other outputs</TableCell><TableCell align="right"><strong>{fmt(agg.box1)}</strong></TableCell></TableRow>
                <TableRow><TableCell>2</TableCell><TableCell>VAT due on EU acquisitions (post-Brexit usually £0)</TableCell><TableCell align="right">{fmt(0)}</TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('output-all')} sx={{ bgcolor: 'grey.50', cursor: 'pointer' }}><TableCell>3</TableCell><TableCell>Total VAT due (Box 1 + Box 2)</TableCell><TableCell align="right"><strong>{fmt(box3)}</strong></TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('input-all')} sx={{ cursor: 'pointer' }}><TableCell>4</TableCell><TableCell>VAT reclaimed on purchases (at invoice / incurred date)</TableCell><TableCell align="right"><strong>{fmt(agg.box4)}</strong></TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('net-all')} sx={{ bgcolor: box5 >= 0 ? 'error.50' : 'success.50', cursor: 'pointer' }}>
                  <TableCell>5</TableCell>
                  <TableCell>Net VAT {box5 >= 0 ? 'payable to HMRC' : 'refundable from HMRC'}</TableCell>
                  <TableCell align="right"><strong>{fmt(Math.abs(box5))}</strong></TableCell>
                </TableRow>
                <TableRow hover onClick={() => openDrill('output-all')} sx={{ cursor: 'pointer' }}><TableCell>6</TableCell><TableCell>Total value of sales (excluding VAT)</TableCell><TableCell align="right">{fmt(agg.box6)}</TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('input-all')} sx={{ cursor: 'pointer' }}><TableCell>7</TableCell><TableCell>Total value of purchases (excluding VAT)</TableCell><TableCell align="right">{fmt(agg.box7)}</TableCell></TableRow>
                <TableRow><TableCell>8</TableCell><TableCell>Total value of EU goods dispatched</TableCell><TableCell align="right">{fmt(0)}</TableCell></TableRow>
                <TableRow><TableCell>9</TableCell><TableCell>Total value of EU goods acquired</TableCell><TableCell align="right">{fmt(0)}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* BREAKDOWN */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Output VAT breakdown</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click any row to see the underlying transactions.</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow hover onClick={() => openDrill('shopify-sales')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Shopify UK sales</TableCell><TableCell align="right">{fmt(agg.shopify_output)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('shopify-refunds')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Shopify UK refunds</TableCell><TableCell align="right" sx={{ color: 'error.main' }}>-{fmt(agg.shopify_refunds)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('amazon-uk')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Amazon.co.uk (net)</TableCell><TableCell align="right">{fmt(agg.amazon_output)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('wholesale-uk')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Wholesale UK sales</TableCell><TableCell align="right">{fmt(agg.wholesale_output)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('input-all')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Reverse-charge self-account</TableCell><TableCell align="right">{fmt(agg.reverse_charge)}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Total Box 1</strong></TableCell><TableCell align="right"><strong>{fmt(agg.box1)}</strong></TableCell></TableRow>
                  <TableRow hover onClick={() => openDrill('wholesale-uk')} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ color: (agg.wholesale_zero_net || 0) > 10000 ? 'warning.main' : 'text.secondary' }}>Wholesale GB rows with no VAT recorded (net — £0 declared)</TableCell>
                    <TableCell align="right" sx={{ color: (agg.wholesale_zero_net || 0) > 10000 ? 'warning.main' : 'text.secondary' }}>{fmt(agg.wholesale_zero_net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Input VAT breakdown (accrual)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click a row to see the underlying transactions. Full per-transaction detail is in the workbook export.</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow hover onClick={() => openDrill('supplier-invoices')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Supplier invoices (SCM, at invoice date)</TableCell><TableCell align="right">{fmt(agg.supplier_input)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('ads-input')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Meta ads (incurred)</TableCell><TableCell align="right">{fmt(agg.meta_ads)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('ads-input')} sx={{ cursor: 'pointer' }}>
                    <TableCell>TikTok ads (incurred)</TableCell><TableCell align="right">{fmt(agg.tiktok_ads)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('ads-input')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Amazon ads (incurred)</TableCell><TableCell align="right">{fmt(agg.amazon_ads)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('amazon-fees')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Amazon seller fees VAT (estimated)</TableCell><TableCell align="right">{fmt(agg.amazon_fees)}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Total Box 4</strong></TableCell><TableCell align="right"><strong>{fmt(agg.box4)}</strong></TableCell></TableRow>
                  <TableRow hover onClick={() => openDrill('input-all')} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ color: 'text.secondary' }}>of which reverse-charge self-account (also in Box 1)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt(agg.reverse_charge)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ACCRUAL vs CASH */}
      {vcAgg && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Accrual vs cash-basis return</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              The cash-basis "UK VAT (cash)" screen claims input VAT when a supplier is <em>paid</em>; this screen claims it
              when the cost is <em>incurred</em> (invoice date). A Box 4 delta is the input VAT the two bases recognise in
              different months, plus any bank-paid spend not yet invoiced through SCM.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell></TableCell>
                  <TableCell align="right">Accrual</TableCell>
                  <TableCell align="right">Cash</TableCell>
                  <TableCell align="right">Delta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell>Output VAT (Box 1)</TableCell><TableCell align="right">{fmt(vcAgg.accrual_box1)}</TableCell><TableCell align="right">{fmt(vcAgg.cash_box1)}</TableCell><TableCell align="right">{fmt(vcAgg.accrual_box1 - vcAgg.cash_box1)}</TableCell></TableRow>
                <TableRow><TableCell>Input VAT (Box 4)</TableCell><TableCell align="right">{fmt(vcAgg.accrual_box4)}</TableCell><TableCell align="right">{fmt(vcAgg.cash_box4)}</TableCell><TableCell align="right" sx={{ color: Math.abs(vcAgg.accrual_box4 - vcAgg.cash_box4) > 1 ? 'warning.main' : 'text.secondary' }}>{fmt(vcAgg.accrual_box4 - vcAgg.cash_box4)}</TableCell></TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* PER-MONTH TABLE */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Per-month detail</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Box 1</TableCell>
                  <TableCell align="right">Box 4</TableCell>
                  <TableCell align="right">Box 5</TableCell>
                  <TableCell align="right">Box 6</TableCell>
                  <TableCell align="right">Box 7</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary || []).map(m => (
                  <TableRow key={m.period_month} hover>
                    <TableCell>{m.period_month}</TableCell>
                    <TableCell align="right">{fmt(m.box1_output_vat)}</TableCell>
                    <TableCell align="right">{fmt(m.box4_input_vat)}</TableCell>
                    <TableCell align="right"><strong>{fmt(m.box5_net_vat)}</strong></TableCell>
                    <TableCell align="right">{fmt(m.box6_net_sales)}</TableCell>
                    <TableCell align="right">{fmt(m.box7_net_purchases)}</TableCell>
                  </TableRow>
                ))}
                {!(summary || []).length && (
                  <TableRow><TableCell colSpan={6}>No VAT activity for this period.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* DRILL-DOWN DRAWER */}
      <Drawer anchor="right" open={!!drill} onClose={closeDrill} PaperProps={{ sx: { width: { xs: '100%', md: '70%' }, maxWidth: 1200 } }}>
        {drill && (() => {
          const cfg = DRILLDOWN_CONFIGS[drill.configKey];
          if (!cfg) return null;
          const total = cfg.sumKey && drill.rows
            ? drill.rows.reduce((s, r) => s + Number(r[cfg.sumKey] || 0), 0)
            : null;
          return (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{cfg.title}</Typography>
                <Tooltip title="Close"><IconButton onClick={closeDrill}><CloseIcon /></IconButton></Tooltip>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {cfg.note} {drill.rows && (
                  <Box component="span" sx={{ display: 'block', mt: 1 }}>
                    Showing <strong>{drill.rows.length}</strong> rows for {periodLabel}
                    {total !== null && (<> &nbsp;·&nbsp; Sum of {cfg.sumKey}: <strong>{fmt(total)}</strong></>)}
                    . For the full list, use <Link component="button" type="button" onClick={downloadExcel} sx={{ verticalAlign: 'baseline' }}>Download Excel <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /></Link>.
                  </Box>
                )}
              </Alert>
              {drill.loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
              {drill.error && <Alert severity="error">{drill.error}</Alert>}
              {!drill.loading && !drill.error && drill.rows && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 220px)' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {cfg.columns.map(c => (
                          <TableCell key={c.key} align={c.align || 'left'} sx={{ bgcolor: 'primary.50', fontWeight: 600 }}>{c.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drill.rows.length === 0 && (
                        <TableRow><TableCell colSpan={cfg.columns.length} align="center" sx={{ py: 4 }}>No transactions in this period.</TableCell></TableRow>
                      )}
                      {drill.rows.map((r, i) => (
                        <TableRow key={i} hover>
                          {cfg.columns.map(c => (
                            <TableCell key={c.key} align={c.align || 'left'} sx={c.bold ? { fontWeight: 600 } : undefined}>
                              {fmtCell(r[c.key], c.money)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })()}
      </Drawer>
    </Box>
  );
}
